import { useState, useRef, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import { 
  Search, 
  ListFilter, 
  Plus,
  RefreshCw,
  Eye,
  Receipt,
  X,
  Filter
} from "lucide-react";

import { getOrdersC, retryOrder } from "../../services/order_services";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { handleSetSearch } from "../../utils/searchDebouncing";
import ARHCustomDataTable from "../../components/dataTable/ARHCustomDataTable";
import CreateOrderModal from "./Components/CreateOrderModal";
import OrderDetailModal from "./Components/OrderDetailModal";
import OrderReceipt from "./Components/OrderReceipt";
import ARHAdvanceFilterModal from "../../components/application/date-picker/FilterModal";
import { ARHCustomDateFiler } from "../../components/application/date-picker/rangeCallernderCard";
import StripePaymentModal from "./Components/CreateOrderModal";

function OrdersC() {
  const userInfo = useSelector((state) => state.auth.user);
  const searchRef = useRef(null);
  const queryClient = useQueryClient();
  const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY);
  const [filters, setFilters] = useState({
    search: "",
    limit: 10,
    page: 1,
    status: "",
    source: "",
    'createdAt[gte]': null,
    'createdAt[lte]': null,
  });
  
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [receiptOpen, setReceiptOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [refetch, setRefetch] = useState(false);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [isAdvanceFilterOpen, setIsAdvanceFilterOpen] = useState(false);

 
  const { data, isLoading, isError, error, refetch: refetchOrders } = useQuery({
    queryKey: ["orders", filters, refetch],
    queryFn: () => getOrdersC(filters),
    keepPreviousData: true
  });


  const retryOrderMutation = useMutation({
    mutationFn: (id) => retryOrder(id),
    onSuccess: (data, id) => {
      // Show success message
      console.log("Order retry enqueued successfully");
      
      // Invalidate and refetch the orders query to update the UI
      queryClient.invalidateQueries({ queryKey: ["orders"] });
      
      // Optional: Show a toast notification
      // toast.success("Order retry requested successfully");
    },
    onError: (error, id) => {
      console.error("Failed to retry order:", error);
      
      // Show error message
      const errorMessage = error.response?.data?.error || "Failed to retry order";
      console.error(errorMessage);
      
      // Optional: Show a toast notification
      // toast.error(errorMessage);
    }
  });

  const handleSearchChange = (value) => {
    setFilters(prev => ({
      ...prev,
      search: value,
      page: 1
    }));
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      page: 1
    }));
  };

  const handleRetryOrder = (id) => {
    retryOrderMutation.mutate(id);
  };

  const handleViewDetails = (order) => {
    setSelectedOrder(order);
    setDetailModalOpen(true);
  };

  const handleViewReceipt = (order) => {
    setSelectedOrder(order);
    setReceiptOpen(true);
  };

  const handleCreateSuccess = () => {
    setCreateModalOpen(false);
    setRefetch(prev => !prev);
  };

  const handleDateRangeApply = (startDate, endDate) => {
    setFilters(prev => ({
      ...prev,
      'createdAt[gte]': startDate,
      'createdAt[lte]': endDate,
      page: 1,
    }));
    setIsDatePickerOpen(false);
  };

  const clearDateFilter = () => {
    setFilters(prev => {
      const newFilter = { ...prev };
      delete newFilter['createdAt[gte]'];
      delete newFilter['createdAt[lte]'];
      return newFilter;
    });
  };

  const formatDateRange = () => {
    if (!filters['createdAt[gte]'] || !filters['createdAt[lte]']) return null;
    const start = new Date(filters['createdAt[gte]']);
    const end = new Date(filters['createdAt[lte]']);
    return `${start.toLocaleDateString()} - ${end.toLocaleDateString()}`;
  };

  const filterConfig = useMemo(() => [
    {
      key: 'status',
      label: 'Status',
      type: 'dropdown',
      options: [
        { value: 'pending', label: 'Pending' },
        { value: 'inprocess', label: 'In Process' },
        { value: 'success', label: 'Success' },
        { value: 'failed', label: 'Failed' },
      ],
      multiSelect: false,
    },
    {
      key: 'source',
      label: 'Source',
      type: 'dropdown',
      options: [
        { value: 'agent_stock', label: 'Agent Stock' },
        { value: 'stripe_card', label: 'Stripe Card' },
      ],
      multiSelect: false,
    },
    {
      key: 'amount',
      label: 'Amount Range',
      type: 'range',
      min: 0,
      max: 100000,
      unit: 'AFN',
    }
  ], []);

  const initialAdvanceValues = useMemo(() => {
    return {
      status: filters.status || '',
      source: filters.source || '',
      amount: {
        min: filters['amount[gte]'] || 0,
        max: filters['amount[lte]'] || 100000,
      }
    };
  }, [filters]);

  const handleApplyAdvanceFilters = (filterValues) => {
    setFilters(prev => {
      const newFilter = { ...prev, page: 1 };
      
      if (filterValues.status) {
        newFilter.status = filterValues.status;
      } else {
        delete newFilter.status;
      }
      
      if (filterValues.source) {
        newFilter.source = filterValues.source;
      } else {
        delete newFilter.source;
      }
      
      if (filterValues.amount) {
        if (filterValues.amount.min > 0) {
          newFilter['amount[gte]'] = filterValues.amount.min;
        } else {
          delete newFilter['amount[gte]'];
        }
        
        if (filterValues.amount.max < 100000) {
          newFilter['amount[lte]'] = filterValues.amount.max;
        } else {
          delete newFilter['amount[lte]'];
        }
      } else {
        delete newFilter['amount[gte]'];
        delete newFilter['amount[lte]'];
      }
      
      return newFilter;
    });
    
    setIsAdvanceFilterOpen(false);
  };

  const clearAllFilters = () => {
    setFilters({
      search: "",
      limit: 10,
      page: 1,
      status: "",
      source: "",
      'createdAt[gte]': null,
      'createdAt[lte]': null,
    });
  };

  const isAnyFilterApplied = useMemo(() => {
    return (
      filters.search || 
      filters['createdAt[gte]'] || 
      filters['createdAt[lte]'] || 
      filters.status || 
      filters.source || 
      filters['amount[gte]'] || 
      filters['amount[lte]']
    );
  }, [filters]);

  const columns = [
    { 
      key: "txnNumber", 
      label: "Transaction ID" 
    },
    { 
      key: "receiver", 
      label: "Receiver" 
    },
    { 
      key: "amount", 
      label: "Amount",
      render: (item) => `${Number(item?.amount || 0).toFixed(2)} ${item?.currency || "AFN"}`
    },
    {
      key: "source",
      label: "Source",
      render: (item) => (
        <span className="capitalize">
          {item?.source?.replace("_", " ") || "-"}
        </span>
      )
    },
    {
      key: "status",
      label: "Status",
      render: (item) => (
        <div className={`px-3 py-1 rounded-full text-xs font-medium ${
          item?.status === "success" || item?.status === "completed"
            ? "bg-green-100 text-green-800"
            : item?.status === "failed"
            ? "bg-red-100 text-red-800"
            : item?.status === "pending" || item?.status === "inprocess"
            ? "bg-yellow-100 text-yellow-800"
            : "bg-gray-100 text-gray-800"
        }`}>
          {item?.status || "-"}
        </div>
      )
    },
    {
      key: "createdAt",
      label: "Created At",
      render: (item) => new Date(item?.createdAt).toLocaleDateString()
    },
    {
      key: "actions",
      label: "Actions",
      render: (item) => (
        <div className="flex space-x-2">
          {/* <button
            onClick={() => handleViewDetails(item)}
            className="text-blue-600 hover:text-blue-800 p-1"
            title="View Details"
          >
            <Eye size={16} />
          </button> */}
          
          <button
            onClick={() => handleViewReceipt(item)}
            className="text-green-600 hover:text-green-800 p-1"
            title="View Receipt"
          >
            <Receipt size={16} />
          </button>
          
          {item.status === "failed" && (
            <button
              onClick={() => handleRetryOrder(item.id)}
              className="text-orange-600 hover:text-orange-800 p-1"
              title="Retry Order"
              disabled={retryOrderMutation.isLoading}
            >
              <RefreshCw 
                size={16} 
                className={retryOrderMutation.isLoading ? "animate-spin" : ""}
              />
            </button>
          )}
        </div>
      )
    }
  ];

  const customEmptyState = (
    <div className="w-full min-h-[200px] flex flex-col items-center justify-center">
      <img 
        src="/images/Search.png" 
        alt="No data found" 
        className="w-[170px] h-[170px]"
      />
      <p className="mt-2 text-gray-500">No Orders Found!</p>
    </div>
  );

  const customErrorState = (
    <div className="w-full min-h-[200px] flex flex-col items-center justify-center">
      <img 
        src="/images/Search.png" 
        alt="Error occurred" 
        className="w-[170px] h-[170px]"
      />
      <div className="text-sm text-red-600 mb-2">
        {error?.message || "Failed to load orders"}
      </div>
      <button
        onClick={() => setRefetch(prev => !prev)}
        className="px-3 py-1 text-sm bg-red-600 text-white rounded hover:bg-red-700"
      >
        Retry
      </button>
    </div>
  );

  return (
    <div className="w-full px-5 py-4 min-h-screen">
<Elements stripe={stripePromise}>
  <StripePaymentModal
    open={createModalOpen}
    onClose={() => setCreateModalOpen(false)}
    onSuccess={handleCreateSuccess}
  />
</Elements>

      
      <OrderDetailModal
        order={selectedOrder}
        open={detailModalOpen}
        onClose={() => setDetailModalOpen(false)}
      />
      
      <OrderReceipt
        order={selectedOrder}
        open={receiptOpen}
        onClose={() => setReceiptOpen(false)}
      />

      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800 dark:text-white">
          Order Management
        </h1>
        {/* <button
          onClick={() => setCreateModalOpen(true)}
          className="bg-[#CD0C02] px-4 py-2 rounded-md text-white flex items-center"
        >
          <Plus size={18} className="mr-2" />
          New Order
        </button> */}
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 mb-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="relative flex-grow">
            <Search size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              ref={searchRef}
              onChange={(e) => handleSetSearch(handleSearchChange, e.target.value, searchRef)}
              type="text"
              placeholder="Search by Transaction ID, Receiver, etc."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-transparent dark:text-white"
            />
          </div>
          
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setIsDatePickerOpen(true)}
              className="max-w-[250px] cursor-pointer w-full md:w-auto border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 h-[42px] flex items-center justify-center gap-2 bg-transparent dark:text-white relative"
            >
              <ListFilter size={16} />
              <span className="truncate">
                {filters['createdAt[gte]'] && filters['createdAt[lte]'] 
                  ? formatDateRange()
                  : "Date Filter"}
              </span>
              
              {filters['createdAt[gte]'] && filters['createdAt[lte]'] && (
                <button 
                  onClick={(e) => {
                    e.stopPropagation();
                    clearDateFilter();
                  }}
                  className="absolute right-2 text-gray-500 hover:text-red-500"
                >
                  <X size={16} />
                </button>
              )}
            </button>
            
            <button
              onClick={() => setIsAdvanceFilterOpen(true)}
              className="max-w-[150px] cursor-pointer w-full md:w-auto border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 h-[42px] flex items-center justify-center gap-2 bg-transparent dark:text-white relative"
            >
              <Filter size={16} />
              Filters
              {isAnyFilterApplied && (
                <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-red-500 flex items-center justify-center">
                  <span className="text-white text-[10px]">!</span>
                </span>
              )}
            </button>
            
            {isAnyFilterApplied && (
              <button
                onClick={clearAllFilters}
                className="max-w-[120px] cursor-pointer w-full md:w-auto border border-red-500 rounded-md px-3 py-2 h-[42px] flex items-center justify-center gap-2 bg-red-50 text-red-600 hover:bg-red-100 transition-colors text-sm"
              >
                Clear All
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
        <ARHCustomDataTable
          data={data?.data || []}
          columns={columns}
          meta={data?.meta || {
            page: filters.page,
            pages: 1,
            total: 0,
            limit: filters.limit
          }}
          loading={isLoading}
          isError={isError}
          error={error}
          onRetry={() => setRefetch(prev => !prev)}
          onPageChange={(page) => setFilters(prev => ({ ...prev, page }))}
          onLimitChange={(limit) => 
            setFilters(prev => ({ ...prev, limit, page: 1 }))
          }
          searchQuery={filters.search}
          emptyState={customEmptyState}
          errorState={customErrorState}
        />
      </div>

      {isDatePickerOpen && (
        <div 
          className="fixed inset-0 bg-black/40 bg-opacity-10 z-50 flex items-center justify-center p-4"
          onClick={() => setIsDatePickerOpen(false)}
        >
          <div 
            className="bg-white dark:bg-gray-800 rounded-xl max-w-full overflow-auto"
            onClick={e => e.stopPropagation()}
          >
            <ARHCustomDateFiler
              onApply={handleDateRangeApply} 
              onClose={() => setIsDatePickerOpen(false)} 
            />
          </div>
        </div>
      )}
      
      {isAdvanceFilterOpen && (
        <ARHAdvanceFilterModal
          isOpen={isAdvanceFilterOpen}
          onClose={() => setIsAdvanceFilterOpen(false)}
          onApply={handleApplyAdvanceFilters}
          filterConfig={filterConfig}
          initialValues={initialAdvanceValues}
        />
      )}
    </div>
  );
}

export default OrdersC;